import React, { useState, useEffect } from 'react';
import { CandidateProfile } from '../../services/candidateServiceApi';
import { applicationApiService } from '../../services/applicationApi';
import StatCard from '../../../shared/components/ui/StatCard';

interface StatsCardsProps {
    profile: CandidateProfile;
}

interface Stats {
    totalApplications: number;
    applicationsInReview: number;
    offersReceived: number;
    savedJobs: number;
}

const StatsCards: React.FC<StatsCardsProps> = () => {
    const [stats, setStats] = useState<Stats>({
        totalApplications: 0,
        applicationsInReview: 0,
        offersReceived: 0,
        savedJobs: 0,
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                // Fetch application statistics
                const statisticsResponse = await applicationApiService.getStatistics();
                const byStatus = statisticsResponse.payload.by_status;
                const total = statisticsResponse.payload.total;

                // Calculate statistics
                const inReview = (byStatus['pending'] || 0) + (byStatus['reviewed'] || 0);
                const offers = byStatus['hired'] || 0;

                // Fetch saved jobs count
                let savedCount = 0;
                try {
                    const savedResponse = await applicationApiService.getSavedJobs();
                    savedCount = savedResponse.payload?.pagination?.total || 0;
                } catch (error) {
                    console.log('Saved jobs not available yet');
                }

                setStats({
                    totalApplications: total,
                    applicationsInReview: inReview,
                    offersReceived: offers,
                    savedJobs: savedCount,
                });
            } catch (error) {
                console.error('Error fetching stats:', error);
                // Keep default values on error
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 rounded-2xl overflow-hidden border border-[#EDEDED] shadow-sm">
                <div className="p-4 md:p-6 bg-white animate-pulse border-b lg:border-b-0 lg:border-r border-[#EDEDED]">
                    <div className="h-8 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="p-4 md:p-6 bg-white animate-pulse border-b lg:border-b-0 lg:border-r border-[#EDEDED]">
                    <div className="h-8 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="p-4 md:p-6 bg-white animate-pulse lg:border-r border-[#EDEDED]">
                    <div className="h-8 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="p-4 md:p-6 bg-white animate-pulse">
                    <div className="h-8 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 rounded-2xl overflow-hidden border border-[#EDEDED] shadow-sm">
            <StatCard
                value={stats.totalApplications}
                label="Total Applications"
                trend={stats.totalApplications > 0 ? "up" : undefined}
                trendValue={stats.totalApplications > 0 ? "Active" : undefined}
            />
            <StatCard
                value={stats.applicationsInReview}
                label="Applications in Review"
                trend={stats.applicationsInReview > 0 ? "up" : undefined}
                trendValue={stats.applicationsInReview > 0 ? "Pending" : undefined}
            />
            <StatCard
                value={stats.offersReceived}
                label="Offers Received"
                trend={stats.offersReceived > 0 ? "up" : undefined}
                trendValue={stats.offersReceived > 0 ? "Success!" : undefined}
            />
            <StatCard
                value={stats.savedJobs}
                label="Saved Jobs"
                trend={stats.savedJobs > 0 ? "up" : undefined}
                trendValue={stats.savedJobs > 0 ? "Bookmarked" : undefined}
                isLast
            />
        </div>
    );
};

export default StatsCards;
